import { useState, useEffect, useCallback, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import { fetchData, VerifyData, deleteData } from '../../apiService';
import useSessionManager from '../../hooks/useSessionManager';
import useAuditLog, { AUDIT_ACTION } from '../../hooks/useAuditLog';
import SaleModal from './Sale/SaleModal';
import PurchaseModal from './Purchase/PurchaseModal';
import PaymentReceiptsModal from './PaymentReceipts/PaymentReceiptsModal';
import {
  TrendingUp, TrendingDown, AccountBalance, Inventory, ShoppingCart,
  Assessment, Warning, Receipt, Payment, Add, ArrowForward,
  Schedule, LocalShipping, RequestQuote, SwapHoriz, AccountBalanceWallet,
  BarChart, PieChart, Notifications, PersonAdd, Storefront, Lock, Star
} from '@mui/icons-material';
import { CircularProgress, Tooltip, Dialog, DialogContent } from '@mui/material';
import './Home.css';

const Home = () => {
  const { companyName, companyId, branchId, token, roleName, userId } = useAuth();
  const { logAudit } = useAuditLog();
  const { validateSession } = useSessionManager();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false); // eslint-disable-line no-unused-vars

  // ── Real KPI state ────────────────────────────────────────────────────────
  const [kpis, setKpis] = useState([
    { key: 'receivable', label: 'Accounts Receivable', value: '₹0', sub: [{ l: 'Overdue', v: '₹0', c: 'neg' }, { l: 'Credit Customers', v: '0' }, { l: 'Due Today', v: '₹0', c: 'warn' }], trend: '0%', up: true },
    { key: 'payable',    label: 'Accounts Payable',    value: '₹0', sub: [{ l: 'Due Today', v: '₹0', c: 'warn' }, { l: 'Overdue', v: '₹0', c: 'neg' }, { l: 'Active Vendors', v: '0' }], trend: '0%', up: false },
    { key: 'revenue',    label: 'Revenue (Month)',      value: '₹0', sub: [{ l: "Today's Sales", v: '₹0', c: 'pos' }, { l: 'Total Bills', v: '0' }, { l: 'Avg Bill', v: '₹0' }], trend: '0%', up: true },
    { key: 'purchase',   label: 'Purchases (Month)',    value: '₹0', sub: [{ l: "Today's Purchase", v: '₹0' }, { l: 'Total Bills', v: '0' }, { l: 'Pending POs', v: '0', c: 'warn' }], trend: '0%', up: false },
    { key: 'stock',      label: 'Stock Value',          value: '₹0', sub: [{ l: 'Low Stock Items', v: '0', c: 'warn' }, { l: 'Out of Stock', v: '0', c: 'neg' }, { l: 'Total SKUs', v: '0' }], trend: '0%', up: true },
    { key: 'cashflow',   label: 'Net Cash Flow',        value: '₹0', sub: [{ l: 'Cash In', v: '₹0', c: 'pos' }, { l: 'Cash Out', v: '₹0', c: 'neg' }, { l: 'Balance', v: '₹0', c: 'pos' }], trend: '0%', up: true },
  ]);

  const [activities, setActivities] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [stockItems, setStockItems] = useState([]);
  const [moduleStats, setModuleStats] = useState([
    { label: 'Sales Orders',    value: 0, sub: '0 pending', path: '/SaleOrderIndex',       color: 'ms-blue' },
    { label: 'Purchase Orders', value: 0, sub: '0 pending', path: '/PurchaseOrderIndex',   color: 'ms-purple' },
    { label: 'Quotations',      value: 0, sub: '0 open',    path: '/QuotationIndex',        color: 'ms-orange' },
    { label: 'Stock Transfers', value: 0, sub: '0 today',   path: '/StockTransferInIndex',  color: 'ms-teal' },
    { label: 'Journal Entries', value: 0, sub: 'this month',path: '/JournalEntryIndex',     color: 'ms-indigo' },
    { label: 'E-Invoices',      value: 0, sub: '0 pending', path: '/EInvoiceIndex',         color: 'ms-green' },
  ]);
  const [receivableAging, setReceivableAging] = useState([
    { label: '0–30 Days',  value: 0, amount: '₹0', color: '#3b82f6' },
    { label: '31–60 Days', value: 0, amount: '₹0', color: '#10b981' },
    { label: '61–90 Days', value: 0, amount: '₹0', color: '#f59e0b' },
    { label: '90+ Days',   value: 0, amount: '₹0', color: '#ef4444' },
  ]);
  const [payableAging, setPayableAging] = useState([
    { label: '0–30 Days',  value: 0, amount: '₹0', color: '#3b82f6' },
    { label: '31–60 Days', value: 0, amount: '₹0', color: '#10b981' },
    { label: '61–90 Days', value: 0, amount: '₹0', color: '#f59e0b' },
    { label: '90+ Days',   value: 0, amount: '₹0', color: '#ef4444' },
  ]);

  // ── Monthly chart data ────────────────────────────────────────────────────
  const [chartData, setChartData] = useState({ sales: new Array(12).fill(0), purchases: new Array(12).fill(0) });
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  // ── Activity View Modal ───────────────────────────────────────────────────
  const [viewModal, setViewModal] = useState({ open: false, type: null });
  const [viewFormData, setViewFormData] = useState({});
  const [viewDetails, setViewDetails] = useState([]);
  const [viewModalLoading, setViewModalLoading] = useState(false);
  const [clickingActivityId, setClickingActivityId] = useState(null);
  const [viewSuppliers, setViewSuppliers] = useState([]);
  const [viewItems, setViewItems] = useState([]);
  const [viewBanks, setViewBanks] = useState([]);
  const [viewCustomers, setViewCustomers] = useState([]);

  // ── Raw reorder items (for Reorder button navigation) ────────────────────
  const reorderItemsRef = useRef([]);
  const [reorderLoading, setReorderLoading] = useState(false);

  // ── Hold Bills ────────────────────────────────────────────────────────────
  const [holdBills, setHoldBills] = useState([]);
  const [holdModal, setHoldModal] = useState({ open: false, bill: null });
  const [holdActionLoading, setHoldActionLoading] = useState(false);

  // ── KPI Popup ─────────────────────────────────────────────────────────────
  const [kpiModal, setKpiModal] = useState({ open: false, kpi: null });

  // ── KPI Sub-label Drill-down Modal ────────────────────────────────────────
  const [subModal, setSubModal] = useState({ open: false, title: '', data: [], columns: [] });
  const [subModalSearch, setSubModalSearch] = useState('');
  const dashDataRef = useRef({ sales: [], purchases: [], payments: [], clients: [], vendors: [], stockList: [], allItems: [], pos: [] });

  // ── Module List Modal ─────────────────────────────────────────────────────
  const [moduleModal, setModuleModal] = useState({ open: false, label: '', data: [] });

  // ── Chart raw data (for bar click drill-down) ─────────────────────────────
  const [chartRawData, setChartRawData] = useState({ sales: [], purchases: [] });
  const [chartModal, setChartModal] = useState({ open: false, month: '', type: '', data: [] });

  // ── Favorite Reports ──────────────────────────────────────────────────────
  const [favoriteReports, setFavoriteReports] = useState([]);

  // ── Quick Actions ─────────────────────────────────────────────────────────
  const quickActions = [
    { icon: <Receipt />,        label: 'New Sale',        path: '/SaleIndex',              color: 'qa-blue' },
    { icon: <ShoppingCart />,   label: 'New Purchase',    path: '/PurchaseIndex',          color: 'qa-purple' },
    { icon: <Payment />,        label: 'Payment/Receipt', path: '/PaymentAndReceiptIndex', color: 'qa-green' },
    { icon: <RequestQuote />,   label: 'New Quotation',   path: '/QuotationIndex',         color: 'qa-orange' },
    { icon: <LocalShipping />,  label: 'Purchase Order',  path: '/PurchaseOrderIndex',     color: 'qa-teal' },
    { icon: <SwapHoriz />,      label: 'Stock Transfer',  path: '/StockTransferInIndex',   color: 'qa-pink' },
    { icon: <AccountBalance />, label: 'Journal Entry',   path: '/JournalEntryIndex',      color: 'qa-indigo' },
    { icon: <PersonAdd />,      label: 'Add Client',      path: '/ClientIndex',            color: 'qa-cyan' },
    { icon: <Storefront />,     label: 'Add Vendor',      path: '/VendorIndex',            color: 'qa-amber' },
    { icon: <Inventory />,      label: 'Add Item',        path: '/ProductItemIndex',       color: 'qa-red' },
  ];

  // ── Helpers ───────────────────────────────────────────────────────────────
  const fmt = (n) => {
    if (n === null || n === undefined || n === '' || isNaN(n)) return '₹0';
    const num = parseFloat(n);
    if (num === 0) return '₹0';
    const abs = Math.abs(num);
    const sign = num < 0 ? '-' : '';
    if (abs >= 10000000) return `${sign}₹${(abs / 10000000).toFixed(2)} Cr`;
    if (abs >= 100000)   return `${sign}₹${(abs / 100000).toFixed(2)} L`;
    if (abs >= 1000) {
      const s = Math.round(abs).toString();
      if (s.length > 3) return `${sign}₹${s.slice(0, s.length - 3)},${s.slice(-3)}`;
    }
    return `${sign}₹${Math.round(abs)}`;
  };

  const today = new Date();
  const todayStr = today.toISOString().split('T')[0];
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
  const yearStart  = new Date(today.getFullYear(), 0, 1).toISOString().split('T')[0];

  const daysBetween = (dateStr) => {
    const d = new Date(dateStr);
    return Math.floor((today - d) / (1000 * 60 * 60 * 24));
  };

  // ── Data Fetching ─────────────────────────────────────────────────────────
  const loadDashboard = useCallback(async () => {
    if (!companyId || !token) return;
    try {
      // Fetch all data in parallel
      const [salesRes, purchasesRes, paymentRes, poRes, soRes, quotRes, stockRes, itemsRes, clientsRes, vendorsRes, stockTransferRes, journalRes] = await Promise.allSettled([
        fetchData('SaleApi',              `/ActiveList/${companyId}/${branchId}/${monthStart}/${todayStr}`, token),
        fetchData('PurchaseApi',          `/ActiveList/${companyId}/${branchId}/${monthStart}/${todayStr}`, token),
        fetchData('PaymentAndReceiptApi', `/ActiveList/${companyId}/${branchId}/${monthStart}/${todayStr}`, token),
        fetchData('PurchaseOrderApi',     `/${companyId}/${branchId}/${yearStart}/${todayStr}`, token),
        fetchData('SaleOrderApi',         `/ActiveList/${companyId}/${branchId}/${yearStart}/${todayStr}`, token),
        fetchData('QuotationApi',         `/ActiveList/${companyId}/${branchId}/${yearStart}/${todayStr}`, token),
        fetchData('SaleApi',              `/ItemStockListDetails/${companyId}/${branchId}`, token),
        fetchData('ItemApi',              `/${companyId}`, token),
        fetchData('ClientAndVendorApi',   `/ClientList/${companyId}`, token),
        fetchData('ClientAndVendorApi',   `/VendorList/${companyId}`, token),
        fetchData('StockInAndOutApi',     `/StockTranferInList/${companyId}/${branchId}/${monthStart}/${todayStr}`, token),
        fetchData('JournalEntryApi',      `/ActiveList/${companyId}/${branchId}/${monthStart}/${todayStr}`, token),
      ]);

      const sales          = salesRes.status          === 'fulfilled' ? (salesRes.value.response_data          || salesRes.value          || []) : [];

      // Fetch IsHold for all sales from detail API
      Promise.allSettled(sales.map(s =>
        fetchData('SaleApi', `/${s.Id}`, token).then(r => {
          const d = r.response_data || r;
          const sd = d.Sale || d;
          return sd.IsHold ? { ...s, IsHold: true, TotalAmount: sd.TotalAmount, CustomerName: sd.CustomerName, BillNumber: sd.BillNumber, BillDate: sd.BillDate } : null;
        }).catch(() => null)
      )).then(results => {
        setHoldBills(results.filter(r => r.status === 'fulfilled' && r.value).map(r => r.value));
      });
      const purchases      = purchasesRes.status      === 'fulfilled' ? (purchasesRes.value.response_data      || purchasesRes.value      || []) : [];
      const payments       = paymentRes.status        === 'fulfilled' ? (paymentRes.value.response_data        || paymentRes.value        || []) : [];
      const pos            = poRes.status             === 'fulfilled' ? (poRes.value.response_data             || poRes.value             || []) : [];
      const sos            = soRes.status             === 'fulfilled' ? (soRes.value.response_data             || soRes.value             || []) : [];
      const quots          = quotRes.status           === 'fulfilled' ? (quotRes.value.response_data           || quotRes.value           || []) : [];
      const stockList      = stockRes.status          === 'fulfilled' ? (stockRes.value.response_data          || stockRes.value          || []) : [];
      const allItems       = itemsRes.status          === 'fulfilled' ? (itemsRes.value.response_data          || itemsRes.value          || []) : [];
      const clients        = clientsRes.status        === 'fulfilled' ? (clientsRes.value.response_data        || clientsRes.value        || []) : [];
      const vendors        = vendorsRes.status        === 'fulfilled' ? (vendorsRes.value.response_data        || vendorsRes.value        || []) : [];
      const stockTransfers = stockTransferRes.status  === 'fulfilled' ? (stockTransferRes.value.response_data  || stockTransferRes.value  || []) : [];
      const journalEntries = journalRes.status        === 'fulfilled' ? (journalRes.value.response_data        || journalRes.value        || []) : [];

      // Store raw data for drill-down
      dashDataRef.current = { sales, purchases, payments, clients, vendors, stockList, allItems, pos };

      // ── Sales KPI ──────────────────────────────────────────────────────────
      const totalRevenue   = sales.reduce((s, i) => s + (parseFloat(i.TotalAmount) || 0), 0);
      const todaySales     = sales.filter(i => i.BillDate?.split('T')[0] === todayStr).reduce((s, i) => s + (parseFloat(i.TotalAmount) || 0), 0);
      const avgBill        = sales.length ? totalRevenue / sales.length : 0;

      // ── Purchase KPI ──────────────────────────────────────────────────────
      const totalPurchase  = purchases.reduce((s, i) => s + (parseFloat(i.TotalAmount) || 0), 0);
      const todayPurchase  = purchases.filter(i => i.BillDate?.split('T')[0] === todayStr).reduce((s, i) => s + (parseFloat(i.TotalAmount) || 0), 0);
      const pendingPOs     = pos.filter(i => !i.IsConverted).length;

      // ── Receivable / Payable ───────────────────────────────────────────────
      const totalReceivable = sales.reduce((s, i) => s + (parseFloat(i.BalanceAmount) || 0), 0);
      const totalPayable    = purchases.reduce((s, i) => s + (parseFloat(i.BalanceAmount) || 0), 0);

      // Aging buckets for receivable (based on BillDate)
      const rBuckets = [0, 0, 0, 0];
      const rAmounts = [0, 0, 0, 0];
      sales.forEach(i => {
        const bal = parseFloat(i.BalanceAmount) || 0;
        if (bal <= 0) return;
        const age = daysBetween(i.BillDate);
        const idx = age <= 30 ? 0 : age <= 60 ? 1 : age <= 90 ? 2 : 3;
        rAmounts[idx] += bal;
      });
      const rTotal = rAmounts.reduce((a, b) => a + b, 0) || 1;
      rAmounts.forEach((v, i) => { rBuckets[i] = Math.round((v / rTotal) * 100); });

      // Aging buckets for payable
      const pBuckets = [0, 0, 0, 0];
      const pAmounts = [0, 0, 0, 0];
      purchases.forEach(i => {
        const bal = parseFloat(i.BalanceAmount) || 0;
        if (bal <= 0) return;
        const age = daysBetween(i.BillDate);
        const idx = age <= 30 ? 0 : age <= 60 ? 1 : age <= 90 ? 2 : 3;
        pAmounts[idx] += bal;
      });
      const pTotal = pAmounts.reduce((a, b) => a + b, 0) || 1;
      pAmounts.forEach((v, i) => { pBuckets[i] = Math.round((v / pTotal) * 100); });

      setReceivableAging([
        { label: '0–30 Days',  value: rBuckets[0], amount: fmt(rAmounts[0]), color: '#3b82f6' },
        { label: '31–60 Days', value: rBuckets[1], amount: fmt(rAmounts[1]), color: '#10b981' },
        { label: '61–90 Days', value: rBuckets[2], amount: fmt(rAmounts[2]), color: '#f59e0b' },
        { label: '90+ Days',   value: rBuckets[3], amount: fmt(rAmounts[3]), color: '#ef4444' },
      ]);
      setPayableAging([
        { label: '0–30 Days',  value: pBuckets[0], amount: fmt(pAmounts[0]), color: '#3b82f6' },
        { label: '31–60 Days', value: pBuckets[1], amount: fmt(pAmounts[1]), color: '#10b981' },
        { label: '61–90 Days', value: pBuckets[2], amount: fmt(pAmounts[2]), color: '#f59e0b' },
        { label: '90+ Days',   value: pBuckets[3], amount: fmt(pAmounts[3]), color: '#ef4444' },
      ]);

      // ── Cash Flow ──────────────────────────────────────────────────────────
      const cashIn  = payments.filter(i => i.IsType === false || i.IsType === 0 || i.Type === 'Receipt').reduce((s, i) => s + (parseFloat(i.Amount || i.TotalAmount) || 0), 0);
      const cashOut = payments.filter(i => i.IsType === true  || i.IsType === 1 || i.Type === 'Payment').reduce((s, i) => s + (parseFloat(i.Amount || i.TotalAmount) || 0), 0);
      const netFlow = cashIn - cashOut;

      // ── Stock ──────────────────────────────────────────────────────────────
      const totalSKUs   = allItems.length;
      const lowStock    = stockList.filter(i => (parseFloat(i.CurrentStock) || 0) > 0 && (parseFloat(i.CurrentStock) || 0) <= (parseFloat(i.MinimumStock) || 0));
      const outOfStock  = stockList.filter(i => (parseFloat(i.CurrentStock) || 0) <= 0);
      const stockValue  = stockList.reduce((s, i) => s + ((parseFloat(i.CurrentStock) || 0) * (parseFloat(i.Rate || i.SaleRate) || 0)), 0);

      // Low stock items for widget
      const lowStockItems = [...outOfStock, ...lowStock].slice(0, 4).map(i => ({
        name:     i.ItemName || i.Name || 'Item',
        current:  Math.round(parseFloat(i.CurrentStock) || 0),
        min:      Math.round(parseFloat(i.MinimumStock) || 0),
        supplier: i.SupplierName || '-',
        pct:      i.MinimumStock > 0 ? Math.min(100, Math.round(((parseFloat(i.CurrentStock) || 0) / parseFloat(i.MinimumStock)) * 100)) : 0,
      }));
      setStockItems(lowStockItems);

      
      // Save raw items for Reorder navigation
      reorderItemsRef.current = [...outOfStock, ...lowStock].map(i => ({
        ItemId:   i.ItemId || i.Id,
        ItemName: i.ItemName || i.Name || '',
        HSNCode:  i.HSNCode || '',
        GSTRate:  parseFloat(i.GSTRate) || 0,
        Rate:     parseFloat(i.PurchaseRate || i.Rate || i.SaleRate) || 0,
      }));

      // ── KPIs ───────────────────────────────────────────────────────────────
      setKpis([
        { key: 'receivable', label: 'Accounts Receivable', value: fmt(totalReceivable),
          sub: [{ l: 'Overdue (90+d)', v: fmt(rAmounts[3]), c: 'neg' }, { l: 'Credit Clients', v: clients.filter(c => c.CreditLimit > 0).length || clients.length }, { l: 'Due (0-30d)', v: fmt(rAmounts[0]), c: 'warn' }],
          trend: `${sales.length} bills`, up: true },
        { key: 'payable', label: 'Accounts Payable', value: fmt(totalPayable),
          sub: [{ l: 'Due (0-30d)', v: fmt(pAmounts[0]), c: 'warn' }, { l: 'Overdue (90+d)', v: fmt(pAmounts[3]), c: 'neg' }, { l: 'Active Vendors', v: vendors.length }],
          trend: `${purchases.length} bills`, up: false },
        { key: 'revenue', label: 'Revenue (Month)', value: fmt(totalRevenue),
          sub: [{ l: "Today's Sales", v: fmt(todaySales), c: 'pos' }, { l: 'Total Bills', v: sales.length }, { l: 'Avg Bill', v: fmt(avgBill) }],
          trend: `${sales.length} bills`, up: true },
        { key: 'purchase', label: 'Purchases (Month)', value: fmt(totalPurchase),
          sub: [{ l: "Today's Purchase", v: fmt(todayPurchase) }, { l: 'Total Bills', v: purchases.length }, { l: 'Pending POs', v: pendingPOs, c: pendingPOs > 0 ? 'warn' : '' }],
          trend: `${purchases.length} bills`, up: false },
        { key: 'stock', label: 'Stock Value', value: fmt(stockValue),
          sub: [{ l: 'Low Stock Items', v: lowStock.length, c: lowStock.length > 0 ? 'warn' : '' }, { l: 'Out of Stock', v: outOfStock.length, c: outOfStock.length > 0 ? 'neg' : '' }, { l: 'Total SKUs', v: totalSKUs }],
          trend: `${totalSKUs} items`, up: true },
        { key: 'cashflow', label: 'Net Cash Flow', value: fmt(netFlow),
          sub: [{ l: 'Cash In', v: fmt(cashIn), c: 'pos' }, { l: 'Cash Out', v: fmt(cashOut), c: 'neg' }, { l: 'Balance', v: fmt(netFlow), c: netFlow >= 0 ? 'pos' : 'neg' }],
          trend: netFlow >= 0 ? '+ve' : '-ve', up: netFlow >= 0 },
      ]);

      // ── Module Stats ───────────────────────────────────────────────────────
      const pendingSOs   = sos.filter(i => !i.IsConverted).length;
      const openQuots    = quots.filter(i => !i.IsConverted).length;
      setModuleStats([
        { label: 'Sales Orders',    value: sos.length,      sub: `${pendingSOs} pending`,   path: '/SaleOrderIndex',      color: 'ms-blue',   listData: sos },
        { label: 'Purchase Orders', value: pos.length,      sub: `${pendingPOs} pending`,   path: '/PurchaseOrderIndex',  color: 'ms-purple', listData: pos },
        { label: 'Quotations',      value: quots.length,    sub: `${openQuots} open`,       path: '/QuotationIndex',      color: 'ms-orange', listData: quots },
        { label: 'Stock Transfers', value: stockTransfers.length, sub: `${stockTransfers.length} this month`, path: '/StockTransferInIndex',color: 'ms-teal', listData: stockTransfers },
        { label: 'Journal Entries', value: journalEntries.length, sub: 'this month',              path: '/JournalEntryIndex',   color: 'ms-indigo', listData: journalEntries },
        { label: 'E-Invoices',      value: sales.filter(i => i.IsEInvoice).length, sub: `${sales.filter(i => i.IsEInvoice && !i.IsVerified).length} pending`, path: '/EInvoiceIndex', color: 'ms-green', listData: sales.filter(i => i.IsEInvoice) },
      ]);

      // ── Recent Activities ──────────────────────────────────────────────────
      const recentSales = sales.slice(0, 3).map(i => ({
        type: 'sale', title: `${[i.BillPrefix, i.BillNumber, i.FinYear].filter(Boolean).join('/')}`, party: i.CustomerName || '-',
        amount: fmt(i.TotalAmount), date: i.BillDate?.split('T')[0] || '',
        status: (parseFloat(i.BalanceAmount) || 0) <= 0 ? 'paid' : i.IsVerified ? 'verified' : 'pending',
        id: i.Id,
        createdBy: i.CreatedByName || '-', createdDate: i.CreatedDate || '',
        modifiedBy: i.ModifiedByName || '-', modifiedDate: i.ModifiedDate || '',
      }));
      const recentPurchases = purchases.slice(0, 2).map(i => ({
        type: 'purchase', title: `${[i.BillPrefix, i.BillNumber, i.FinYear].filter(Boolean).join('/')}`, party: i.MasterName || '-',
        amount: fmt(i.TotalAmount), date: i.BillDate?.split('T')[0] || '',
        status: (parseFloat(i.BalanceAmount) || 0) <= 0 ? 'paid' : i.IsVerified ? 'verified' : 'pending',
        id: i.Id,
        isConvertToGoodReceipt: i.IsConvertToGoodReceipt ?? false,
        isVerified: i.IsVerified ?? false,
        createdBy: i.CreatedByName || '-', createdDate: i.CreatedDate || '',
        modifiedBy: i.ModifiedByName || '-', modifiedDate: i.ModifiedDate || '',
      }));
      const recentPayments = payments.slice(0, 1).map(i => ({
        type: 'payment', title: `${(i.IsType === false || i.IsType === 0) ? 'RPT' : 'PMT'}/${i.BillNumber || ''}/${i.FinYear || ''}`, party: i.MasterName || '-',
        amount: fmt(i.Amount || i.TotalAmount), date: i.BillDate?.split('T')[0] || '',
        status: 'completed',
        id: i.Id,
        createdBy: i.CreatedByName || '-', createdDate: i.CreatedDate || '',
        modifiedBy: i.ModifiedByName || '-', modifiedDate: i.ModifiedDate || '',
      }));
      setActivities([...recentSales, ...recentPurchases, ...recentPayments].slice(0, 6));

      // ── Alerts ─────────────────────────────────────────────────────────────
      const newAlerts = [];
      const overdueReceivables = sales.filter(i => (parseFloat(i.BalanceAmount) || 0) > 0 && daysBetween(i.BillDate) > 30);
      if (overdueReceivables.length > 0) {
        const top = overdueReceivables[0];
        newAlerts.push({ type: 'overdue', title: 'Overdue Invoice', detail: `${top.CustomerName || 'Customer'} — ${fmt(top.BalanceAmount)} overdue by ${daysBetween(top.BillDate)} days`, action: 'Send Reminder' });
      }
      const duePayables = purchases.filter(i => {
        const bal = parseFloat(i.BalanceAmount) || 0;
        if (bal <= 0 || !i.PaymentDueDate) return false;
        const dueDate = new Date(i.PaymentDueDate);
        const daysUntilDue = Math.floor((dueDate - today) / (1000 * 60 * 60 * 24));
        return daysUntilDue <= 3 && daysUntilDue >= 0;
      });
      if (duePayables.length > 0) {
        const top = duePayables[0];
        const dueDate = new Date(top.PaymentDueDate);
        const daysUntilDue = Math.floor((dueDate - today) / (1000 * 60 * 60 * 24));
        const dueText = daysUntilDue === 0 ? 'due today' : `due in ${daysUntilDue} day${daysUntilDue > 1 ? 's' : ''}`;
        newAlerts.push({ type: 'payment', title: 'Vendor Payment Due', detail: `${top.MasterName || 'Vendor'} — ${fmt(top.BalanceAmount)} ${dueText}`, action: 'Pay Now', masterId: top.MasterId, masterName: top.MasterName, amount: top.BalanceAmount });
      }
      if (lowStock.length > 0) {
        newAlerts.push({ type: 'stock', title: 'Low Stock Alert', detail: `${lowStock[0].ItemName || 'Item'} — only ${Math.round(parseFloat(lowStock[0].CurrentStock) || 0)} units remaining`, action: 'Reorder' });
      }
      if (outOfStock.length > 0) {
        newAlerts.push({ type: 'stock', title: 'Out of Stock', detail: `${outOfStock.length} item(s) out of stock`, action: 'Reorder' });
      }
      if (pendingPOs > 0) {
        newAlerts.push({ type: 'po', title: 'PO Pending', detail: `${pendingPOs} Purchase Order(s) not yet converted`, action: 'Review' });
      }
      setAlerts(newAlerts.slice(0, 5));

      // ── Monthly Chart ──────────────────────────────────────────────────────
      // Fetch full year data for chart
      const [ySalesRes, yPurchasesRes] = await Promise.allSettled([
        fetchData('SaleApi',     `/ActiveList/${companyId}/${branchId}/${yearStart}/${todayStr}`, token),
        fetchData('PurchaseApi', `/ActiveList/${companyId}/${branchId}/${yearStart}/${todayStr}`, token),
      ]);
      const ySales     = ySalesRes.status     === 'fulfilled' ? (ySalesRes.value.response_data     || ySalesRes.value     || []) : [];
      const yPurchases = yPurchasesRes.status === 'fulfilled' ? (yPurchasesRes.value.response_data || yPurchasesRes.value || []) : [];

      const monthlySales     = new Array(12).fill(0);
      const monthlyPurchases = new Array(12).fill(0);
      ySales.forEach(i => {
        const m = new Date(i.BillDate).getMonth();
        if (!isNaN(m)) monthlySales[m] += parseFloat(i.TotalAmount) || 0;
      });
      yPurchases.forEach(i => {
        const m = new Date(i.BillDate).getMonth();
        if (!isNaN(m)) monthlyPurchases[m] += parseFloat(i.TotalAmount) || 0;
      });
      setChartData({ sales: monthlySales, purchases: monthlyPurchases });
      setChartRawData({ sales: ySales, purchases: yPurchases });

    } catch (err) {
      console.error('Dashboard load error:', err);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, branchId, token]);

  useEffect(() => {
    if (!validateSession()) return;
    setLoading(true);
    loadDashboard().finally(() => setLoading(false));
  }, [validateSession, loadDashboard]);

  // ── Load Favorite Reports ─────────────────────────────────────────────────
  useEffect(() => {
    if (!userId || !token) return;
    fetchData("FavoriteReportApi", `/${userId}`, token)
      .then((res) => {
        const data = res.response_data || res || [];
        const activeItems = data.filter((item) => item.IsActive && item.Name);
        setFavoriteReports(activeItems);
      })
      .catch(() => setFavoriteReports([]));
  }, [userId, token]);

  const handleRefresh = () => { // eslint-disable-line no-unused-vars
    setRefreshing(true);
    loadDashboard().finally(() => setRefreshing(false));
  };

  // ── Activity click → open view modal ─────────────────────────────────────
  const handleActivityClick = async (activity) => {
    if (!activity.id) return;
    setClickingActivityId(activity.id);
    setViewModalLoading(true);
    setViewModal({ open: true, type: activity.type });

    try {
      if (activity.type === 'sale') {
        const [res, customersRes, banksRes] = await Promise.allSettled([
          fetchData('SaleApi', `/${activity.id}`, token),
          viewCustomers.length ? Promise.resolve({ response_data: viewCustomers }) : fetchData('ClientAndVendorApi', `/ClientList/${companyId}`, token),
          viewBanks.length ? Promise.resolve({ response_data: viewBanks }) : fetchData('BankApi', `/${companyId}`, token),
        ]);
        const freshData = res.status === 'fulfilled' ? (res.value.response_data || res.value) : null;
        if (freshData) {
          const saleData = freshData.Sale || freshData;
          const details = freshData.SaleDetails || [];
          setViewFormData({
            Id: saleData.Id, CompanyId: saleData.CompanyId, BranchId: saleData.BranchId,
            MasterId: saleData.MasterId, CustomerName: saleData.CustomerName || '',
            InvoiceTypeId: saleData.InvoiceTypeId || 0, InvoiceTypeName: saleData.InvoiceTypeName || '',
            BillNumber: saleData.BillNumber, BillPrefix: saleData.BillPrefix || '',
            FinYear: saleData.FinYear || '', BillDate: saleData.BillDate?.split('T')[0] || '',
            ClearingDate: saleData.ClearingDate?.split('T')[0] || '',
            GstCalculationType: saleData.GstCalculationType || 1,
            GstCalculationTypeName: saleData.GstCalculationTypeName || 'Excluding Tax',
            Amount: saleData.Amount || 0, DiscountAmount: saleData.DiscountAmount || 0,
            TaxableAmount: saleData.TaxableAmount || 0, CGSTAmount: saleData.CGSTAmount || 0,
            SGSTAmount: saleData.SGSTAmount || 0, IGSTAmount: saleData.IGSTAmount || 0,
            FreightAmount: saleData.FreightAmount || 0, FreightGSTRate: saleData.FreightGSTRate || 0,
            FreightGSTAmount: saleData.FreightGSTAmount || 0, PackingAmount: saleData.PackingAmount || 0,
            PackingGSTRate: saleData.PackingGSTRate || 0, PackingGSTAmount: saleData.PackingGSTAmount || 0,
            TCSAmount: saleData.TCSAmount || 0, RoundOff: saleData.RoundOff || 0,
            TotalAmount: saleData.TotalAmount || saleData.GrandTotal || 0,
            GrandTotal: saleData.GrandTotal || saleData.TotalAmount || 0,
            CashAmount: saleData.CashAmount || 0, UPIAmount: saleData.UPIAmount || 0,
            CardAmount: saleData.CardAmount || 0, BankId: saleData.BankId || null,
            BalanceAmount: saleData.BalanceAmount || 0, Remarks: saleData.Remarks || '',
            DispatchFrom: saleData.DispatchFrom || '', DispatchAddress1: saleData.DispatchAddress1 || '',
            DispatchAddress2: saleData.DispatchAddress2 || '', DispatchLocation: saleData.DispatchLocation || '',
            DispatchPincode: saleData.DispatchPincode || '', ShipTo: saleData.ShipTo || '',
            ShipGSTNo: saleData.ShipGSTNo || '', ShipToAddress1: saleData.ShipToAddress1 || '',
            ShipToAddress2: saleData.ShipToAddress2 || '', ShipToLocation: saleData.ShipToLocation || '',
            ShipToPincode: saleData.ShipToPincode || '', VehicleNumber: saleData.VehicleNumber || '',
            TransporterName: saleData.TransporterName || '', TransporterGST: saleData.TransporterGST || '',
            Distance: saleData.Distance || 0, IsVerified: saleData.IsVerified || false,
            IsActive: saleData.IsActive ?? true,
            SalesManId: saleData.SalesManId || '', SalesManName: saleData.SalesManName || '',
            CreatedByName: saleData.CreatedByName || '', CreatedDate: saleData.CreatedDate || '',
            ModifiedByName: saleData.ModifiedByName || '', ModifiedDate: saleData.ModifiedDate || '',
          });
          setViewDetails(details.map(d => ({
            Id: d.Id || 0, ItemId: d.ItemId, ItemName: d.ItemName || '',
            HSNCode: d.HSNCode || '', GSTRate: d.GSTRate || 0, Quantity: d.Quantity || 0,
            Rate: d.Rate || 0, Amount: d.Amount || 0, DiscountPercentage: d.DiscountPercentage || 0,
            DiscountAmount: d.DiscountAmount || 0, TaxableAmount: d.TaxableAmount || 0,
            CGSTAmount: d.CGSTAmount || 0, SGSTAmount: d.SGSTAmount || 0, IGSTAmount: d.IGSTAmount || 0,
            TotalAmount: d.TotalAmount || 0, IsActive: d.IsActive ?? true,
          })));
        }
        if (customersRes.status === 'fulfilled' && !viewCustomers.length) {
          setViewCustomers(customersRes.value.response_data || customersRes.value || []);
        }
        if (banksRes.status === 'fulfilled' && !viewBanks.length) {
          setViewBanks(banksRes.value.response_data || banksRes.value || []);
        }
      } else if (activity.type === 'purchase') {
        const [res, suppliersRes, itemsRes] = await Promise.allSettled([
          fetchData('PurchaseApi', `/${activity.id}`, token),
          viewSuppliers.length ? Promise.resolve({ response_data: viewSuppliers }) : fetchData('ClientAndVendorApi', `/VendorList/${companyId}`, token),
          viewItems.length ? Promise.resolve({ response_data: viewItems }) : fetchData('ItemApi', `/${companyId}`, token),
        ]);
        const freshData = res.status === 'fulfilled' ? (res.value.response_data || res.value) : null;
        if (freshData) {
          const p = freshData.Purchase || freshData;
          const details = freshData.PurchaseDetails || [];
          setViewFormData({
            Id: p.Id, CompanyId: p.CompanyId, BranchId: p.BranchId,
            MasterId: p.MasterId, MasterName: p.MasterName || '',
            InvoiceTypeId: p.InvoiceTypeId || 0, InvoiceTypeName: p.InvoiceTypeName || '',
            GSTNumber: p.GSTNumber || '', BillDate: p.BillDate?.split('T')[0] || '',
            ClearingDate: p.ClearingDate?.split('T')[0] || '', BillNumber: p.BillNumber || '',
            GstCalculationType: p.GstCalculationType || 1,
            GstCalculationTypeName: p.GstCalculationTypeName || 'Excluding Tax',
            Amount: p.Amount || 0, DiscountAmount: p.DiscountAmount || 0,
            TaxableAmount: p.TaxableAmount || 0, CGSTAmount: p.CGSTAmount || 0,
            SGSTAmount: p.SGSTAmount || 0, IGSTAmount: p.IGSTAmount || 0,
            FreightAmount: p.FreightAmount || 0, FreightGSTRate: p.FreightGSTRate || 0,
            FreightGSTAmount: p.FreightGSTAmount || 0, PackingAmount: p.PackingAmount || 0,
            PackingGSTRate: p.PackingGSTRate || 0, PackingGSTAmount: p.PackingGSTAmount || 0,
            TCSAmount: p.TCSAmount || 0, RoundOff: p.RoundOff || 0,
            TotalAmount: p.TotalAmount || 0, PaidAmount: p.PaidAmount || 0,
            BalanceAmount: p.BalanceAmount || 0, Remarks: p.Remarks || '',
            IsActive: p.IsActive ?? true, IsVerified: p.IsVerified ?? activity.isVerified ?? false,
            IsConvertToGoodReceipt: p.IsConvertToGoodReceipt ?? activity.isConvertToGoodReceipt ?? false,
            CreatedByName: p.CreatedByName || '', CreatedDate: p.CreatedDate || '',
            ModifiedByName: p.ModifiedByName || '', ModifiedDate: p.ModifiedDate || '',
          });
          setViewDetails(details.map(d => ({
            Id: d.Id || 0, ItemId: d.ItemId, ItemName: d.ItemName || '',
            HSNCode: d.HSNCode || '', GSTRate: d.GSTRate || 0,
            ReceivedQuantity: d.ReceivedQuantity || 0, Rate: d.Rate || 0,
            Amount: d.Amount || 0, DiscountPercentage: d.DiscountPercentage || 0,
            DiscountAmount: d.DiscountAmount || 0, TaxableAmount: d.TaxableAmount || 0,
            CGSTAmount: d.CGSTAmount || 0, SGSTAmount: d.SGSTAmount || 0, IGSTAmount: d.IGSTAmount || 0,
            TotalAmount: d.TotalAmount || 0, IsActive: d.IsActive ?? true,
          })));
        }
        if (suppliersRes.status === 'fulfilled' && !viewSuppliers.length) {
          setViewSuppliers(suppliersRes.value.response_data || suppliersRes.value || []);
        }
        if (itemsRes.status === 'fulfilled' && !viewItems.length) {
          setViewItems(itemsRes.value.response_data || itemsRes.value || []);
        }
      } else if (activity.type === 'payment') {
        const res = await fetchData('PaymentAndReceiptApi', `/PaymentData/${activity.id}`, token);
        const freshData = res.response_data || res;
        if (freshData) {
          const p = freshData.PaymentAndReceipt || freshData;
          setViewFormData({
            ...p,
            BillDate: p.BillDate ? p.BillDate.split('T')[0] : '',
            ChequeDate: p.ChequeDate ? p.ChequeDate.split('T')[0] : '',
            MasterId: p.MasterId || '',
          });

          const purchaseDetails = freshData.PaymentAndReceiptPurchase || [];
          const saleDetails = freshData.PaymentAndReceiptSale || [];

          if (p.IsType && purchaseDetails.length > 0) {
            setViewDetails(purchaseDetails.map(d => ({
              Id: d.PurchaseId,
              Description: d.PurchaseDescription || d.Description || `Purchase - ${d.PurchaseId}`,
              Amount: parseFloat(d.PurchaseAmount || d.BillAmount || 0),
              PaidAmount: parseFloat(d.PaidAmount || 0),
              PendingAmount: parseFloat(d.PendingAmount || 0),
              BillDate: d.BillDate || p.BillDate,
              Type: 'Purchase',
              PaymentAmount: parseFloat(d.Amount || 0),
              IsSelected: true,
            })));
          } else if (!p.IsType && saleDetails.length > 0) {
            setViewDetails(saleDetails.map(d => ({
              Id: d.SaleId,
              Description: d.SaleDescription || d.Description || `Sale - ${d.SaleId}`,
              Amount: parseFloat(d.SaleAmount || d.BillAmount || 0),
              PaidAmount: parseFloat(d.PaidAmount || 0),
              PendingAmount: parseFloat(d.PendingAmount || 0),
              BillDate: d.BillDate || p.BillDate,
              Type: 'Sale',
              PaymentAmount: parseFloat(d.Amount || 0),
              IsSelected: true,
            })));
          } else {
            setViewDetails([]);
          }
        }
      }
    } catch (err) {
      console.error('Activity click error:', err);
    } finally {
      setViewModalLoading(false);
      setClickingActivityId(null);
    }
  };

  const handleCloseViewModal = () => {
    setViewModal({ open: false, type: null });
    setViewFormData({});
    setViewDetails([]);
  };

  // ── Hold Bill Actions ─────────────────────────────────────────────────────
  const handleApproveBill = async () => {
    if (!holdModal.bill) return;
    setHoldActionLoading(true);
    try {
      await VerifyData('SaleApi', holdModal.bill.Id, token);
      setHoldBills(prev => prev.filter(b => b.Id !== holdModal.bill.Id));
      setHoldModal({ open: false, bill: null });
    } catch (e) {
      console.error('Approve failed', e);
    }
    setHoldActionLoading(false);
  };

  const handleCancelBill = async () => {
    if (!holdModal.bill) return;
    setHoldActionLoading(true);
    try {
      await deleteData('SaleApi', holdModal.bill.Id, token);
      logAudit(AUDIT_ACTION.DELETE, 'SaleApi', holdModal.bill.Id, { Id: holdModal.bill.Id }, null);
      setHoldBills(prev => prev.filter(b => b.Id !== holdModal.bill.Id));
      setHoldModal({ open: false, bill: null });
    } catch (e) {
      console.error('Cancel failed', e);
    }
    setHoldActionLoading(false);
  };

  const handleReorder = () => {
    setReorderLoading(true);
    setTimeout(() => {
      history.push('/PurchaseOrderIndex', { reorderItems: reorderItemsRef.current });
    }, 300);
  };

  // ── Sublabel Drill-down Handler ────────────────────────────────────────────
  const handleSubClick = (kpiKey, subLabel, e) => {
    e.stopPropagation(); // prevent parent card click
    const { sales, purchases, payments, clients, vendors, stockList, allItems, pos } = dashDataRef.current;
    let title = '';
    let data = [];
    let columns = [];

    // Helper to format bill number as Prefix/BillNumber/FinYear
    const fmtBill = (items) => items.map(i => ({
      ...i,
      FullBillNo: [i.BillPrefix, i.BillNumber, i.FinYear].filter(Boolean).join('/')
    }));

    if (kpiKey === 'receivable') {
      if (subLabel.includes('Overdue') || subLabel.includes('90+')) {
        title = 'Overdue Receivables (90+ Days)';
        data = fmtBill(sales.filter(i => (parseFloat(i.BalanceAmount) || 0) > 0 && daysBetween(i.BillDate) > 90));
        columns = [
          { key: 'FullBillNo', label: 'Bill #' },
          { key: 'CustomerName', label: 'Customer' },
          { key: 'BillDate', label: 'Date', format: 'date' },
          { key: 'TotalAmount', label: 'Amount', format: 'currency' },
          { key: 'BalanceAmount', label: 'Balance', format: 'currency' },
        ];
      } else if (subLabel.includes('Credit')) {
        title = 'Credit Clients';
        data = clients.filter(c => c.CreditLimit > 0).length > 0
          ? clients.filter(c => c.CreditLimit > 0)
          : clients;
        columns = [
          { key: 'Name', label: 'Client Name' },
          { key: 'ContactNumber1', label: 'Mobile' },
          { key: 'CreditLimit', label: 'Credit Limit', format: 'currency' },
          { key: 'GSTNumber', label: 'GST No' },
        ];
      } else if (subLabel.includes('Due') || subLabel.includes('0-30') || subLabel.includes('0–30')) {
        title = 'Due Receivables (0-30 Days)';
        data = fmtBill(sales.filter(i => {
          const bal = parseFloat(i.BalanceAmount) || 0;
          if (bal <= 0) return false;
          const age = daysBetween(i.BillDate);
          return age <= 30;
        }));
        columns = [
          { key: 'FullBillNo', label: 'Bill #' },
          { key: 'CustomerName', label: 'Customer' },
          { key: 'BillDate', label: 'Date', format: 'date' },
          { key: 'TotalAmount', label: 'Amount', format: 'currency' },
          { key: 'BalanceAmount', label: 'Balance', format: 'currency' },
        ];
      }
    } else if (kpiKey === 'payable') {
      if (subLabel.includes('Due') || subLabel.includes('0-30') || subLabel.includes('0–30')) {
        title = 'Due Payables (0-30 Days)';
        data = fmtBill(purchases.filter(i => {
          const bal = parseFloat(i.BalanceAmount) || 0;
          if (bal <= 0) return false;
          const age = daysBetween(i.BillDate);
          return age <= 30;
        }));
        columns = [
          { key: 'FullBillNo', label: 'Bill #' },
          { key: 'MasterName', label: 'Vendor' },
          { key: 'BillDate', label: 'Date', format: 'date' },
          { key: 'TotalAmount', label: 'Amount', format: 'currency' },
          { key: 'BalanceAmount', label: 'Balance', format: 'currency' },
        ];
      } else if (subLabel.includes('Overdue') || subLabel.includes('90+')) {
        title = 'Overdue Payables (90+ Days)';
        data = fmtBill(purchases.filter(i => (parseFloat(i.BalanceAmount) || 0) > 0 && daysBetween(i.BillDate) > 90));
        columns = [
          { key: 'FullBillNo', label: 'Bill #' },
          { key: 'MasterName', label: 'Vendor' },
          { key: 'BillDate', label: 'Date', format: 'date' },
          { key: 'TotalAmount', label: 'Amount', format: 'currency' },
          { key: 'BalanceAmount', label: 'Balance', format: 'currency' },
        ];
      } else if (subLabel.includes('Vendor')) {
        title = 'Active Vendors';
        data = vendors;
        columns = [
          { key: 'Name', label: 'Vendor Name' },
          { key: 'ContactNumber1', label: 'Mobile' },
          { key: 'GSTNumber', label: 'GST No' },
          { key: 'PinCode', label: 'Pin Code' },
        ];
      }
    } else if (kpiKey === 'revenue') {
      if (subLabel.includes('Today')) {
        title = "Today's Sales";
        data = fmtBill(sales.filter(i => i.BillDate?.split('T')[0] === todayStr));
        columns = [
          { key: 'FullBillNo', label: 'Bill #' },
          { key: 'CustomerName', label: 'Customer' },
          { key: 'TotalAmount', label: 'Amount', format: 'currency' },
        ];
      } else if (subLabel.includes('Total Bills')) {
        title = 'All Sales This Month';
        data = fmtBill(sales);
        columns = [
          { key: 'FullBillNo', label: 'Bill #' },
          { key: 'CustomerName', label: 'Customer' },
          { key: 'BillDate', label: 'Date', format: 'date' },
          { key: 'TotalAmount', label: 'Amount', format: 'currency' },
        ];
      } else if (subLabel.includes('Avg')) {
        title = 'Pending Receivables';
        data = fmtBill(sales.filter(i => (parseFloat(i.BalanceAmount) || 0) > 0));
        columns = [
          { key: 'FullBillNo', label: 'Bill #' },
          { key: 'CustomerName', label: 'Customer' },
          { key: 'BillDate', label: 'Date', format: 'date' },
          { key: 'BalanceAmount', label: 'Balance Due', format: 'currency' },
        ];
      }
    } else if (kpiKey === 'purchase') {
      if (subLabel.includes('Today')) {
        title = "Today's Purchases";
        data = fmtBill(purchases.filter(i => i.BillDate?.split('T')[0] === todayStr));
        columns = [
          { key: 'FullBillNo', label: 'Bill #' },
          { key: 'MasterName', label: 'Vendor' },
          { key: 'TotalAmount', label: 'Amount', format: 'currency' },
        ];
      } else if (subLabel.includes('Total Bills')) {
        title = 'All Purchases This Month';
        data = fmtBill(purchases);
        columns = [
          { key: 'FullBillNo', label: 'Bill #' },
          { key: 'MasterName', label: 'Vendor' },
          { key: 'BillDate', label: 'Date', format: 'date' },
          { key: 'TotalAmount', label: 'Amount', format: 'currency' },
        ];
      } else if (subLabel.includes('Pending')) {
        title = 'Pending Purchase Orders';
        data = fmtBill(pos.filter(i => !i.IsConverted));
        columns = [
          { key: 'FullBillNo', label: 'PO #' },
          { key: 'MasterName', label: 'Vendor' },
          { key: 'BillDate', label: 'Date', format: 'date' },
          { key: 'TotalAmount', label: 'Amount', format: 'currency' },
        ];
      }
    } else if (kpiKey === 'stock') {
      if (subLabel.includes('Low Stock')) {
        title = 'Low Stock Items';
        data = stockList.filter(i => (parseFloat(i.CurrentStock) || 0) > 0 && (parseFloat(i.CurrentStock) || 0) <= (parseFloat(i.MinimumStock) || 0));
        columns = [
          { key: 'ItemName', label: 'Item' },
          { key: 'CurrentStock', label: 'Current Stock' },
          { key: 'MinimumStock', label: 'Min Stock' },
          { key: 'Rate', label: 'Rate', format: 'currency' },
        ];
      } else if (subLabel.includes('Out of Stock')) {
        title = 'Out of Stock Items';
        data = stockList.filter(i => (parseFloat(i.CurrentStock) || 0) <= 0);
        columns = [
          { key: 'ItemName', label: 'Item' },
          { key: 'CurrentStock', label: 'Current Stock' },
          { key: 'MinimumStock', label: 'Min Stock' },
          { key: 'Rate', label: 'Rate', format: 'currency' },
        ];
      } else if (subLabel.includes('SKU')) {
        title = 'All Items (SKUs)';
        data = allItems;
        columns = [
          { key: 'Name', label: 'Item Name' },
          { key: 'Code', label: 'Code' },
          { key: 'HSNCode', label: 'HSN' },
          { key: 'GSTRate', label: 'GST %' },
        ];
      }
    } else if (kpiKey === 'cashflow') {
      if (subLabel.includes('Cash In')) {
        title = 'Cash In (Receipts)';
        data = payments.filter(i => i.IsType === false || i.IsType === 0 || i.Type === 'Receipt').map(i => ({
          ...i,
          FullBillNo: `RPT/${i.BillNumber || ''}/${i.FinYear || ''}`
        }));
        columns = [
          { key: 'FullBillNo', label: 'Receipt #' },
          { key: 'MasterName', label: 'Party' },
          { key: 'BillDate', label: 'Date', format: 'date' },
          { key: 'Amount', label: 'Amount', format: 'currency' },
        ];
      } else if (subLabel.includes('Cash Out')) {
        title = 'Cash Out (Payments)';
        data = payments.filter(i => i.IsType === true || i.IsType === 1 || i.Type === 'Payment').map(i => ({
          ...i,
          FullBillNo: `PMT/${i.BillNumber || ''}/${i.FinYear || ''}`
        }));
        columns = [
          { key: 'FullBillNo', label: 'Payment #' },
          { key: 'MasterName', label: 'Party' },
          { key: 'BillDate', label: 'Date', format: 'date' },
          { key: 'Amount', label: 'Amount', format: 'currency' },
        ];
      } else if (subLabel.includes('Balance')) {
        title = 'All Payments & Receipts';
        data = payments.map(i => ({
          ...i,
          FullBillNo: `${(i.IsType === false || i.IsType === 0) ? 'RPT' : 'PMT'}/${i.BillNumber || ''}/${i.FinYear || ''}`,
          TypeLabel: (i.IsType === false || i.IsType === 0) ? 'Receipt' : (i.IsType === true || i.IsType === 1) ? 'Payment' : (i.Type || '-')
        }));
        columns = [
          { key: 'FullBillNo', label: 'Bill #' },
          { key: 'MasterName', label: 'Party' },
          { key: 'BillDate', label: 'Date', format: 'date' },
          { key: 'Amount', label: 'Amount', format: 'currency' },
          { key: 'TypeLabel', label: 'Type' },
        ];
      }
    }

    if (data.length > 0 || title) {
      setSubModal({ open: true, title, data, columns });
    }
  };

  const formatPct = (pct) => {
    const color = pct <= 20 ? '#ef4444' : pct <= 40 ? '#f59e0b' : '#10b981';
    return { width: `${Math.min(pct, 100)}%`, background: color };
  };

  const maxVal = Math.max(...chartData.sales, ...chartData.purchases, 1);

  // ── Shared modal styles ───────────────────────────────────────────────────
  const S = {
    overlay: { position:'fixed',inset:0,background:'rgba(15,23,42,0.45)',backdropFilter:'blur(5px)',zIndex:1300,display:'flex',alignItems:'center',justifyContent:'center',padding:16 },
    modal:   { background:'#fff',borderRadius:14,width:'100%',maxWidth:560,maxHeight:'80vh',display:'flex',flexDirection:'column',boxShadow:'0 16px 48px rgba(0,0,0,0.18)',overflow:'hidden' },
    hdr:     { padding:'12px 16px',display:'flex',alignItems:'center',justifyContent:'space-between',flexShrink:0 },
    hdrTitle:{ color:'#fff',fontWeight:700,fontSize:14,letterSpacing:-0.2 },
    hdrSub:  { color:'rgba(255,255,255,0.65)',fontSize:10.5,marginTop:2 },
    xBtn:    { background:'rgba(255,255,255,0.18)',border:'none',borderRadius:8,width:28,height:28,cursor:'pointer',color:'#fff',fontSize:17,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,lineHeight:1 },
    tableWrap:{ overflowY:'auto',flex:1 },
    table:   { width:'100%',borderCollapse:'collapse',fontSize:12 },
    thead:   { background:'#f8fafc',position:'sticky',top:0,zIndex:1 },
    th:      { padding:'7px 12px',color:'#a1a1aa',fontWeight:700,fontSize:10,textTransform:'uppercase',letterSpacing:0.5,borderBottom:'1px solid #e4e4e7',whiteSpace:'nowrap' },
    tr:      { borderBottom:'1px solid #f4f4f5',transition:'background 0.12s' },
    tdMuted: { padding:'7px 12px',color:'#d4d4d8',fontSize:11,width:28,flexShrink:0 },
    tdBold:  { padding:'7px 12px',color:'#18181b',fontWeight:600,maxWidth:150,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' },
    tdMono:  { padding:'7px 12px',color:'#52525b',fontFamily:'monospace',fontSize:11.5 },
    tdDate:  { padding:'7px 12px',color:'#71717a',whiteSpace:'nowrap',fontSize:11.5 },
    tdAmt:   { padding:'7px 12px',textAlign:'right',color:'#18181b',fontWeight:700,fontSize:12 },
    badge:   { fontSize:9.5,padding:'2px 7px',borderRadius:20,fontWeight:700,display:'inline-block' },
    badgeGreen:  { background:'#dcfce7',color:'#15803d' },
    badgeBlue:   { background:'#e0f2fe',color:'#0369a1' },
    badgeYellow: { background:'#fef9c3',color:'#92400e' },
    footer:  { padding:'10px 14px',borderTop:'1px solid #f4f4f5',display:'flex',justifyContent:'flex-end',gap:8,background:'#fafafa',flexShrink:0 },
    btnGhost:{ padding:'6px 14px',borderRadius:8,border:'1px solid #e4e4e7',background:'#fff',cursor:'pointer',fontSize:12,color:'#52525b',fontWeight:600,display:'flex',alignItems:'center',gap:4 },
    btnPrimary:{ padding:'6px 16px',borderRadius:8,border:'none',color:'#fff',cursor:'pointer',fontSize:12,fontWeight:700,display:'flex',alignItems:'center',gap:4 },
  };

  if (loading) {
    return (
      <div style={{ minHeight: 'calc(100vh - 48px)', background: '#f0f4f8', position: 'relative' }}>
        <div style={{
          position: 'fixed', inset: 0,
          background: 'rgba(240, 244, 248, 0.6)',
          backdropFilter: 'blur(4px)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          zIndex: 9999, gap: 36,
        }}>
        {/* Logo blocks — 3D flip reveal in sequence */}
        <div style={{ position: 'relative', width: 90, height: 74, perspective: 400 }}>
          {/* Green → Red */}
          <div style={{
            position: 'absolute', width: 36, height: 36, background: '#cc2b2b',
            borderRadius: 6, bottom: 0, left: 0,
            animation: 'tileFlip 2s ease-in-out infinite',
            animationDelay: '0s',
            boxShadow: '0 0 24px rgba(204,43,43,0.6)',
          }} />
          {/* Blue → Green */}
          <div style={{
            position: 'absolute', width: 30, height: 52, background: '#3aaa5c',
            borderRadius: 6, bottom: 0, left: 30,
            animation: 'tileFlip 2s ease-in-out infinite',
            animationDelay: '0.3s',
            boxShadow: '0 0 24px rgba(58,170,92,0.6)',
          }} />
          {/* Red → Blue */}
          <div style={{
            position: 'absolute', width: 34, height: 34, background: '#2d6db5',
            borderRadius: 6, top: 0, right: 0,
            animation: 'tileFlip 2s ease-in-out infinite',
            animationDelay: '0.6s',
            boxShadow: '0 0 24px rgba(45,109,181,0.6)',
          }} />
          {/* Orange */}
          <div style={{
            position: 'absolute', width: 34, height: 34, background: '#e8a020',
            borderRadius: 6, bottom: 0, right: 0,
            animation: 'tileFlip 2s ease-in-out infinite',
            animationDelay: '0.9s',
            boxShadow: '0 0 24px rgba(232,160,32,0.6)',
          }} />
        </div>

        {/* Company name with shimmer */}
        <div style={{ textAlign: 'center', px: 2 }}>
          <div style={{
            fontSize: 24, fontWeight: 800, color: 'transparent',
            letterSpacing: 3, textTransform: 'uppercase',
            fontFamily: 'Verdana, sans-serif',
            background: 'linear-gradient(90deg, #cc2b2b, #3aaa5c, #2d6db5, #e8a020, #cc2b2b)',
            backgroundSize: '300% auto',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            animation: 'shimmerText 2.5s linear infinite',
            maxWidth: '90vw',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {window.innerWidth < 600 ? 'MADAVANA' : (companyName || 'ERP')}
          </div>
          <div style={{ fontSize: 11, color: 'rgba(30,40,80,0.5)', marginTop: 8, letterSpacing: 2, textTransform: 'uppercase' }}>
            Loading Dashboard
            <span style={{ animation: 'dots 1.5s steps(3,end) infinite' }}>...</span>
          </div>
        </div>

        {/* Dot indicators */}
        <div style={{ display: 'flex', gap: 8 }}>
          {['#cc2b2b','#3aaa5c','#2d6db5','#e8a020'].map((c, i) => (
            <div key={i} style={{
              width: 8, height: 8, borderRadius: '50%', background: c,
              animation: 'dotPulse 1.2s ease-in-out infinite',
              animationDelay: `${i * 0.2}s`,
            }} />
          ))}
        </div>

        <style>{`
          @keyframes tileFlip {
            0%   { transform: rotateY(0deg) scale(1); opacity: 1; }
            25%  { transform: rotateY(90deg) scale(0.8); opacity: 0.3; }
            50%  { transform: rotateY(180deg) scale(1); opacity: 1; }
            75%  { transform: rotateY(270deg) scale(0.8); opacity: 0.3; }
            100% { transform: rotateY(360deg) scale(1); opacity: 1; }
          }
          @keyframes shimmerText {
            0%   { background-position: 0% center; }
            100% { background-position: 300% center; }
          }
          @keyframes dotPulse {
            0%, 100% { transform: scale(1); opacity: 0.4; }
            50%       { transform: scale(1.6); opacity: 1; }
          }
          @keyframes dots {
            0%   { content: '.'; }
            33%  { content: '..'; }
            66%  { content: '...'; }
          }
        `}</style>
        
        </div>
      </div>
    );
  }

  return (
    <div className="dash">
      {/* ── KPI Cards ───────────────────────────────────────────────────── */}
      <div className="dash-kpi-grid">
        {kpis.map((k) => (
          <div key={k.key} className={`dash-kpi-card kpi-${k.key}`}
            onClick={() => setKpiModal({ open: true, kpi: k })}
            style={{ cursor: 'pointer' }}>
            <div className="kpi-top">
              <div className={`kpi-icon kpi-icon-${k.key}`}>
                {k.key === 'receivable' && <AccountBalance />}
                {k.key === 'payable'    && <Payment />}
                {k.key === 'revenue'    && <TrendingUp />}
                {k.key === 'purchase'   && <ShoppingCart />}
                {k.key === 'stock'      && <Inventory />}
                {k.key === 'cashflow'   && <Assessment />}
              </div>
              <div className="kpi-meta">
                <span className="kpi-label">{k.label}</span>
                <span className={`kpi-trend ${k.up ? 'trend-up' : 'trend-down'}`}>
                  {k.up ? <TrendingUp /> : <TrendingDown />} {k.trend}
                </span>
              </div>
            </div>
            <div className="kpi-value">{k.value}</div>
            <div className="kpi-subs">
              {k.sub.map((s, i) => (
                <div key={i} className="kpi-sub-row" 
                  onClick={(e) => handleSubClick(k.key, s.l, e)}
                  style={{ cursor: 'pointer', borderRadius: 4, transition: 'background 0.15s' }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.04)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                  <span className="kpi-sub-label">{s.l}</span>
                  <span className={`kpi-sub-val ${s.c === 'pos' ? 'c-pos' : s.c === 'neg' ? 'c-neg' : s.c === 'warn' ? 'c-warn' : ''}`}>{s.v}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* ── Main Grid ───────────────────────────────────────────────────── */}
      <div className="dash-main-grid">

        {/* ── Left Column ─────────────────────────────────────────────── */}
        <div className="dash-col-left">
          <div className="dash-card">
            <div className="card-hdr">
              <span className="card-hdr-title"><Add fontSize="small" /> Quick Actions</span>
            </div>
            <div className="qa-grid">
              {quickActions.map((a, i) => (
                <Tooltip key={i} title={a.label} placement="top" arrow>
                  <button className={`qa-btn ${a.color}`} onClick={() => history.push(a.path)}>
                    <span className="qa-icon">{a.icon}</span>
                    <span className="qa-label">{a.label}</span>
                  </button>
                </Tooltip>
              ))}
            </div>
          </div>

          {/* ── Favorite Reports Card ─────────────────────────────────── */}
          {favoriteReports.length > 0 && (
            <div className="dash-card">
              <div className="card-hdr">
                <span className="card-hdr-title"><Star fontSize="small" style={{ color: '#f5a623' }} /> Favorites</span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 0 }}>
                {favoriteReports.map((item) => {
                  const displayName = item.Name?.replace('/', '').replace(/([A-Z])/g, ' $1').trim() || item.Name;
                  return (
                    <div
                      key={item.Id}
                      onClick={() => history.push(item.Name)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '8px 12px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #f3f4f6',
                        width: '100%',
                        transition: 'background 0.1s',
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#fffbeb'}
                      onMouseLeave={(e) => e.currentTarget.style.background = ''}
                    >
                      <Star style={{ fontSize: 14, color: '#f5a623', flexShrink: 0 }} />
                      <span style={{ fontSize: 12, fontWeight: 500, color: '#1f2937' }}>{displayName}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="dash-card">
            <div className="card-hdr">
              <span className="card-hdr-title"><BarChart fontSize="small" /> Module Overview</span>
            </div>
            <div className="module-grid">
              {moduleStats.map((m, i) => (
                <div key={i} className={`module-stat ${m.color}`}
                  onClick={() => m.listData?.length > 0
                    ? setModuleModal({ open: true, label: m.label, data: m.listData })
                    : history.push(m.path)
                  }
                  style={{ cursor: 'pointer' }}
                >
                  <span className="module-val">{m.value}</span>
                  <span className="module-label">{m.label}</span>
                  <span className="module-sub">{m.sub}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Center Column ───────────────────────────────────────────── */}
        <div className="dash-col-center">
          <div className="dash-card chart-card-tall">
            <div className="card-hdr">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <span className="card-hdr-title"><BarChart fontSize="small" /> Sales vs Purchases</span>
                <span style={{ fontSize: 10, color: '#64748b', fontWeight: 500 }}>
                  {new Date().toLocaleString('en-IN', { month: 'long', year: 'numeric' })}
                </span>
              </div>
              <div className="chart-legend-row">
                <span className="cl-dot" style={{ background: '#2c3e6e' }}></span><span>Sales</span>
                <span className="cl-dot" style={{ background: '#e67e22' }}></span><span>Purchases</span>
              </div>
            </div>
            <div className="bar-chart-wrap">
              <div className="bar-chart-inner">
              {months.map((m, i) => (
                <div key={i} className="bar-col">
                  <div className="bar-pair">
                    <Tooltip title={`Sales: ${fmt(chartData.sales[i])}`}>
                      <div className="bar bar-sales"
                        style={{ height: `${(chartData.sales[i] / maxVal) * 100}%`, cursor: 'pointer' }}
                        onClick={() => {
                          const data = chartRawData.sales.filter(r => new Date(r.BillDate).getMonth() === i);
                          setChartModal({ open: true, month: m, type: 'Sales', data });
                        }}
                      ></div>
                    </Tooltip>
                    <Tooltip title={`Purchase: ${fmt(chartData.purchases[i])}`}>
                      <div className="bar bar-purchase"
                        style={{ height: `${(chartData.purchases[i] / maxVal) * 100}%`, cursor: 'pointer' }}
                        onClick={() => {
                          const data = chartRawData.purchases.filter(r => new Date(r.BillDate).getMonth() === i);
                          setChartModal({ open: true, month: m, type: 'Purchases', data });
                        }}
                      ></div>
                    </Tooltip>
                  </div>
                  <span className="bar-month">{m}</span>
                </div>
              ))}
              </div>
            </div>
          </div>

          <div className="dash-two-col">
            {/* Recent Activities */}
            <div className="dash-card">
              <div className="card-hdr">
                <span className="card-hdr-title"><Schedule fontSize="small" /> Recent Activities</span>
                <button className="card-hdr-link" onClick={() => history.push('/SaleIndex')}>View All <ArrowForward fontSize="inherit" /></button>
              </div>
              <div className="activity-list">
                {activities.length === 0 && <div className="dash-empty">No recent activities</div>}
                {activities.map((a, i) => (
                  <div key={i} className="activity-row"
                    onClick={() => handleActivityClick(a)}
                    style={{ cursor: a.type !== 'payment' ? 'pointer' : 'default', opacity: clickingActivityId === a.id ? 0.7 : 1 }}
                  >
                    <div className={`act-icon act-${a.type}`} style={{ position: 'relative' }}>
                      {clickingActivityId === a.id
                        ? <CircularProgress size={18} sx={{ color: 'inherit' }} />
                        : <>
                            {a.type === 'sale'     && <Receipt />}
                            {a.type === 'payment'  && <Payment />}
                            {a.type === 'purchase' && <ShoppingCart />}
                            {a.type === 'po'       && <LocalShipping />}
                            {a.type === 'receipt'  && <AccountBalanceWallet />}
                          </>
                      }
                    </div>
                    <div className="act-info">
                      <span className="act-title">{a.title}</span>
                      <span className="act-party">{a.party}</span>
                    </div>
                    <div className="act-right">
                      <span className="act-amount">{a.amount}</span>
                      <span className={`act-status status-${a.status}`}>{a.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Alerts */}
            <div className="dash-card">
              <div className="card-hdr">
                <span className="card-hdr-title"><Notifications fontSize="small" /> Alerts</span>
                {alerts.length > 0 && <span className="alert-badge">{alerts.length}</span>}
              </div>
              <div className="alert-list">
                {alerts.length === 0 && <div className="dash-empty">No alerts — all clear ✓</div>}
                {alerts.map((a, i) => (
                  <div key={i} className={`alert-row alert-${a.type}`}>
                    <div className="alert-icon-wrap">
                      {a.type === 'overdue' && <Warning />}
                      {a.type === 'payment' && <Payment />}
                      {a.type === 'stock'   && <Inventory />}
                      {a.type === 'po'      && <LocalShipping />}
                    </div>
                    <div className="alert-body">
                      <span className="alert-title">{a.title}</span>
                      <span className="alert-detail">{a.detail}</span>
                    </div>
                    <button className="alert-action" onClick={
                      a.type === 'stock' ? handleReorder :
                      a.type === 'payment' ? () => history.push('/PaymentAndReceiptIndex', { openCreate: true, masterId: a.masterId, masterName: a.masterName, amount: a.amount }) :
                      a.type === 'overdue' ? () => history.push('/PaymentAndReceiptIndex', { openCreate: true }) :
                      a.type === 'po' ? () => history.push('/PurchaseOrderIndex') :
                      undefined
                    } disabled={a.type === 'stock' && reorderLoading}>
                      {a.type === 'stock' && reorderLoading
                        ? <CircularProgress size={10} sx={{ color: 'inherit' }} />
                        : a.action}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ── Right Column ────────────────────────────────────────────── */}
        <div className="dash-col-right">
          <div className="dash-card">
            <div className="card-hdr">
              <span className="card-hdr-title"><PieChart fontSize="small" /> Receivable Ageing</span>
              <span className="card-hdr-total">{kpis.find(k => k.key === 'receivable')?.value}</span>
            </div>
            <div className="aging-section">
              {receivableAging.map((a, i) => (
                <div key={i} className="aging-row">
                  <span className="aging-label">{a.label}</span>
                  <div className="aging-bar-track">
                    <div className="aging-bar-fill" style={{ width: `${a.value}%`, background: a.color }}></div>
                  </div>
                  <span className="aging-pct" style={{ color: a.color }}>{a.value}%</span>
                  <span className="aging-amt">{a.amount}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="dash-card">
            <div className="card-hdr">
              <span className="card-hdr-title"><PieChart fontSize="small" /> Payable Ageing</span>
              <span className="card-hdr-total">{kpis.find(k => k.key === 'payable')?.value}</span>
            </div>
            <div className="aging-section">
              {payableAging.map((a, i) => (
                <div key={i} className="aging-row">
                  <span className="aging-label">{a.label}</span>
                  <div className="aging-bar-track">
                    <div className="aging-bar-fill" style={{ width: `${a.value}%`, background: a.color }}></div>
                  </div>
                  <span className="aging-pct" style={{ color: a.color }}>{a.value}%</span>
                  <span className="aging-amt">{a.amount}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="dash-card">
            <div className="card-hdr">
              <span className="card-hdr-title" style={{ color: '#dc2626' }}><Lock fontSize="small" /> Hold Bills</span>
              {holdBills.length > 0 && <span className="alert-badge" style={{ background: '#dc2626' }}>{holdBills.length}</span>}
            </div>
            <div className="alert-list">
              {holdBills.length === 0 && <div className="dash-empty">No bills on hold</div>}
              {holdBills.map((b, i) => (
                <div key={i} className="alert-row" style={{ background: '#fff1f2', borderLeft: '3px solid #dc2626', cursor: (roleName === 'CompanyAdmin' || roleName === 'SuperAdmin') ? 'pointer' : 'default' }}
                  onClick={() => { if (roleName === 'CompanyAdmin' || roleName === 'SuperAdmin') setHoldModal({ open: true, bill: b }); }}>
                  <div className="alert-icon-wrap" style={{ color: '#dc2626' }}><Warning /></div>
                  <div className="alert-body">
                    <span className="alert-title" style={{ color: '#dc2626' }}>Bill #{b.BillNumber || '-'}</span>
                    <span className="alert-detail">{b.CustomerName} — ₹{parseFloat(b.TotalAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                  <button className="alert-action" style={{ background: (roleName === 'CompanyAdmin' || roleName === 'SuperAdmin') ? '#dc2626' : '#9ca3af', color: '#fff', border: 'none', borderRadius: 6, padding: '3px 10px', fontSize: 11, cursor: (roleName === 'CompanyAdmin' || roleName === 'SuperAdmin') ? 'pointer' : 'not-allowed', opacity: (roleName === 'CompanyAdmin' || roleName === 'SuperAdmin') ? 1 : 0.6 }} disabled={!(roleName === 'CompanyAdmin' || roleName === 'SuperAdmin')}>Review</button>
                </div>
              ))}
            </div>
          </div>

          <div className="dash-card">
            <div className="card-hdr">
              <span className="card-hdr-title"><Warning fontSize="small" /> Low Stock Items</span>
              <button className="card-hdr-link" onClick={() => history.push('/ProductItemIndex')}>View All <ArrowForward fontSize="inherit" /></button>
            </div>
            <div className="stock-list">
              {stockItems.length === 0 && <div className="dash-empty">No low stock items</div>}
              {stockItems.map((s, i) => (
                <div key={i} className="stock-row">
                  <div className="stock-info">
                    <span className="stock-name">{s.name}</span>
                    <span className="stock-supplier">{s.supplier}</span>
                  </div>
                  <div className="stock-bar-wrap">
                    <div className="stock-bar-track">
                      <div className="stock-bar-fill" style={formatPct(s.pct)}></div>
                    </div>
                    <span className="stock-qty">{s.current}/{s.min}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Activity Loading Modal ───────────────────────────────────────── */}
      {viewModal.open && viewModalLoading && (
        <Dialog open maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, py: 5 }}>
            <CircularProgress size={40} sx={{ color: viewModal.type === 'sale' ? '#4e342e' : viewModal.type === 'payment' ? '#7c3aed' : '#0d8392' }} />
            <span style={{ fontSize: 13, color: '#64748b', fontWeight: 500 }}>
              Loading {viewModal.type === 'sale' ? 'sale' : viewModal.type === 'payment' ? 'payment' : 'purchase'} details...
            </span>
          </DialogContent>
        </Dialog>
      )}

      {/* ── Sale View Modal ──────────────────────────────────────────────── */}
      {viewModal.open && viewModal.type === 'sale' && !viewModalLoading && (
        <SaleModal
          open={viewModal.open}
          onClose={handleCloseViewModal}
          modalAction="view"
          formData={viewFormData}
          setFormData={setViewFormData}
          saleDetails={viewDetails}
          setSaleDetails={setViewDetails}
          onSave={() => {}}
          onVerify={() => {}}
          customers={viewCustomers}
          items={viewItems}
          banks={viewBanks}
          loading={viewModalLoading}
          freightPackingGST={{ freight: null, packing: null }}
          companyId={companyId}
          branchId={branchId}
          token={token}
        />
      )}

      {/* ── Purchase View Modal ──────────────────────────────────────────── */}
      {viewModal.open && viewModal.type === 'purchase' && !viewModalLoading && (
        <PurchaseModal
          open={viewModal.open}
          onClose={handleCloseViewModal}
          modalAction="view"
          formData={viewFormData}
          setFormData={setViewFormData}
          purchaseDetails={viewDetails}
          setPurchaseDetails={setViewDetails}
          onSave={() => {}}
          onVerify={() => {}}
          onToGoodReceipt={() => {}}
          suppliers={viewSuppliers}
          items={viewItems}
          loading={viewModalLoading}
          freightPackingGST={{ freight: null, packing: null }}
          companyId={companyId}
          token={token}
          setLoading={() => {}}
          setLoadingMessage={() => {}}
          onFetchItems={() => {}}
        />
      )}

      {/* ── Payment/Receipt View Modal ───────────────────────────────────── */}
      {viewModal.open && viewModal.type === 'payment' && !viewModalLoading && (
        <PaymentReceiptsModal
          open={viewModal.open}
          onClose={handleCloseViewModal}
          modalAction="view"
          formData={viewFormData}
          setFormData={setViewFormData}
          paymentReceiptDetails={viewDetails}
          setPaymentReceiptDetails={setViewDetails}
          onSave={() => {}}
          masters={[{ Id: viewFormData.MasterId, Name: viewFormData.MasterName }]}
          banks={viewBanks}
          purchases={viewFormData.IsType ? viewDetails : []}
          sales={!viewFormData.IsType ? viewDetails : []}
          paymentMethods={[]}
          gstRates={[]}
          loading={viewModalLoading}
          companyId={companyId}
          branchId={branchId}
          token={token}
          fetchPurchases={() => {}}
          fetchSales={() => {}}
          fetchBillsForMaster={() => {}}
          fetchMaxBillNo={() => {}}
          fromDate={monthStart}
          toDate={todayStr}
        />
      )}
      {/* ── Module List Modal ────────────────────────────────────────────── */}
      {moduleModal.open && (
        <div style={S.overlay} onClick={() => setModuleModal({ open:false,label:'',data:[] })}>
          <div style={S.modal} onClick={e => e.stopPropagation()}>
            <div style={{ ...S.hdr, background:'linear-gradient(135deg,#0ea5e9,#0284c7)' }}>
              <div>
                <div style={S.hdrTitle}>{moduleModal.label}</div>
                <div style={S.hdrSub}>{moduleModal.data.length} records</div>
              </div>
              <button style={S.xBtn} onClick={() => setModuleModal({ open:false,label:'',data:[] })}>×</button>
            </div>
            <div style={S.tableWrap}>
              <table style={S.table}>
                <thead>
                  <tr style={S.thead}>
                    {['#','Party','Bill No','Date','Amount',...(moduleModal.label==='Journal Entries'||moduleModal.label==='Stock Transfers'?[]:['Status'])].map((h,i) => (
                      <th key={i} style={{ ...S.th, textAlign: i>=4?'right':'left' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {moduleModal.data.map((row, idx) => (
                    <tr key={idx} style={S.tr}
                      onMouseEnter={e => e.currentTarget.style.background='#f8fafc'}
                      onMouseLeave={e => e.currentTarget.style.background=''}>
                      <td style={S.tdMuted}>{idx+1}</td>
                      <td style={S.tdBold}>{row.MasterName||row.CustomerName||row.VendorName||'-'}</td>
                      <td style={S.tdMono}>{row.PurchaseOrderNo||(row.BillPrefix||''+(row.BillNumber||row.BillNo||'-'))}</td>
                      <td style={S.tdDate}>{(row.PurchaseOrderDate||row.BillDate||'').split('T')[0]||'-'}</td>
                      <td style={S.tdAmt}>{fmt(row.TotalAmount||row.GrandTotal||row.Amount||0)}</td>
                      {moduleModal.label!=='Journal Entries'&&moduleModal.label!=='Stock Transfers'&&(
                        <td style={{ ...S.tdAmt }}>
                          <span style={{ ...S.badge, ...(row.IsConverted?S.badgeGreen:row.IsVerified?S.badgeBlue:S.badgeYellow) }}>
                            {row.IsConverted?'Converted':row.IsVerified?'Verified':'Pending'}
                          </span>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={S.footer}>
              <button style={S.btnGhost} onClick={() => { setModuleModal({open:false,label:'',data:[]}); history.push(moduleStats.find(m=>m.label===moduleModal.label)?.path||'/'); }}>View All</button>
              <button style={{ ...S.btnPrimary, background:'linear-gradient(135deg,#0ea5e9,#0284c7)' }} onClick={() => setModuleModal({open:false,label:'',data:[]})}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Chart Drill-down Modal ───────────────────────────────────────── */}
      {chartModal.open && (
        <div style={S.overlay} onClick={() => setChartModal({open:false,month:'',type:'',data:[]})}>
          <div style={S.modal} onClick={e => e.stopPropagation()}>
            <div style={{ ...S.hdr, background: chartModal.type==='Sales' ? 'linear-gradient(135deg,#0ea5e9,#0284c7)' : 'linear-gradient(135deg,#f97316,#c2410c)' }}>
              <div>
                <div style={S.hdrTitle}>{chartModal.type} — {chartModal.month}</div>
                <div style={S.hdrSub}>{chartModal.data.length} bills · Total: {fmt(chartModal.data.reduce((s,r)=>s+(parseFloat(r.TotalAmount)||0),0))}</div>
              </div>
              <button style={S.xBtn} onClick={() => setChartModal({open:false,month:'',type:'',data:[]})}>×</button>
            </div>
            <div style={S.tableWrap}>
              {chartModal.data.length === 0
                ? <div style={{ padding:32, textAlign:'center', color:'#a1a1aa', fontSize:12 }}>No records for {chartModal.month}</div>
                : <table style={S.table}>
                    <thead>
                      <tr style={S.thead}>
                        {['#', chartModal.type==='Sales'?'Customer':'Vendor', 'Bill No', 'Date', 'Amount'].map((h,i) => (
                          <th key={i} style={{ ...S.th, textAlign: i===4?'right':'left' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {chartModal.data.map((row,idx) => (
                        <tr key={idx} style={S.tr}
                          onMouseEnter={e=>e.currentTarget.style.background='#f8fafc'}
                          onMouseLeave={e=>e.currentTarget.style.background=''}>
                          <td style={S.tdMuted}>{idx+1}</td>
                          <td style={S.tdBold}>{row.CustomerName||row.MasterName||'-'}</td>
                          <td style={S.tdMono}>{(row.BillPrefix||'')+(row.BillNumber||'-')}</td>
                          <td style={S.tdDate}>{row.BillDate?row.BillDate.split('T')[0]:'-'}</td>
                          <td style={S.tdAmt}>{fmt(row.TotalAmount||0)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ background:'#f4f6fb', borderTop:'2px solid #e4e4e7' }}>
                        <td colSpan={4} style={{ padding:'8px 12px', fontWeight:700, color:'#18181b', fontSize:12 }}>Total</td>
                        <td style={{ padding:'8px 12px', textAlign:'right', fontWeight:800, color:'#18181b', fontSize:13 }}>
                          {fmt(chartModal.data.reduce((s,r)=>s+(parseFloat(r.TotalAmount)||0),0))}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
              }
            </div>
            <div style={S.footer}>
              <button style={{ ...S.btnPrimary, background: chartModal.type==='Sales' ? 'linear-gradient(135deg,#0ea5e9,#0284c7)' : 'linear-gradient(135deg,#f97316,#c2410c)' }}
                onClick={() => setChartModal({open:false,month:'',type:'',data:[]})}>Close</button>
            </div>
          </div>
        </div>
      )}
      {/* ── Hold Bill Approval Modal ─────────────────────────────────────── */}
      {holdModal.open && holdModal.bill && (
        <div style={S.overlay} onClick={() => !holdActionLoading && setHoldModal({ open: false, bill: null })}>
          <div style={{ ...S.modal, maxWidth: 400 }} onClick={e => e.stopPropagation()}>
            <div style={{ ...S.hdr, background:'linear-gradient(135deg,#dc2626,#991b1b)', gap:10 }}>
              <Warning style={{ color:'#fff', fontSize:20, flexShrink:0 }} />
              <div>
                <div style={S.hdrTitle}>Bill on Hold</div>
                <div style={S.hdrSub}>Credit limit / days exceeded</div>
              </div>
              <button style={{ ...S.xBtn, marginLeft:'auto' }} onClick={() => setHoldModal({ open: false, bill: null })}>×</button>
            </div>
            <div style={{ padding:'14px 16px' }}>
              <div style={{ background:'#fff1f2', border:'1px solid #fecdd3', borderRadius:8, padding:'10px 12px', marginBottom:12, fontSize:12 }}>
                <div style={{ fontWeight:700, color:'#18181b', marginBottom:3 }}>Bill #{holdModal.bill.BillNumber || '-'}</div>
                <div style={{ color:'#71717a' }}>Customer: <b style={{ color:'#18181b' }}>{holdModal.bill.CustomerName}</b></div>
                <div style={{ color:'#71717a' }}>Amount: <b style={{ color:'#dc2626' }}>₹{parseFloat(holdModal.bill.TotalAmount||0).toLocaleString('en-IN',{minimumFractionDigits:2})}</b></div>
                <div style={{ color:'#71717a' }}>Date: {holdModal.bill.BillDate?.split('T')[0]}</div>
              </div>
              <div style={{ fontSize:12, color:'#52525b', marginBottom:14, lineHeight:1.5 }}>
                Approve this bill and override the credit restriction?
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                <button onClick={handleApproveBill} disabled={holdActionLoading}
                  style={{ ...S.btnPrimary, width:'100%', justifyContent:'center', background:'linear-gradient(135deg,#22c55e,#15803d)', padding:'9px 0' }}>
                  {holdActionLoading ? <CircularProgress size={13} sx={{ color:'#fff' }} /> : '✓'}&nbsp; Yes, Approve
                </button>
                <button onClick={handleCancelBill} disabled={holdActionLoading}
                  style={{ ...S.btnPrimary, width:'100%', justifyContent:'center', background:'linear-gradient(135deg,#dc2626,#991b1b)', padding:'9px 0' }}>
                  {holdActionLoading ? <CircularProgress size={13} sx={{ color:'#fff' }} /> : '✕'}&nbsp; Cancel Bill
                </button>
                <button onClick={() => setHoldModal({ open: false, bill: null })} disabled={holdActionLoading}
                  style={{ ...S.btnGhost, width:'100%', justifyContent:'center' }}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* ── KPI Sub-label Drill-down Modal ──────────────────────────────── */}
      {subModal.open && (() => {
        const q = subModalSearch.toLowerCase();
        const filtered = q ? subModal.data.filter(row =>
          subModal.columns.some(col => {
            const val = row[col.key];
            if (val == null) return false;
            return String(val).toLowerCase().includes(q);
          })
        ) : subModal.data;
        return (
        <div style={{ ...S.overlay, zIndex:1600 }} onClick={() => { setSubModal({ open:false, title:'', data:[], columns:[] }); setSubModalSearch(''); }}>
          <div style={{ ...S.modal, maxWidth:700, animation:'kpiPopIn 0.22s cubic-bezier(0.34,1.56,0.64,1)' }} onClick={e=>e.stopPropagation()}>
            <div style={{ ...S.hdr, background:'linear-gradient(135deg,#1e293b,#334155)' }}>
              <div>
                <div style={S.hdrTitle}>{subModal.title}</div>
                <div style={S.hdrSub}>{filtered.length} of {subModal.data.length} record{subModal.data.length !== 1 ? 's' : ''}</div>
              </div>
              <button style={S.xBtn} onClick={() => { setSubModal({ open:false, title:'', data:[], columns:[] }); setSubModalSearch(''); }}>×</button>
            </div>
            <div style={{ padding:'8px 14px', borderBottom:'1px solid #f4f4f5', background:'#fafafa' }}>
              <input
                type="text"
                placeholder="Search..."
                value={subModalSearch}
                onChange={e => setSubModalSearch(e.target.value)}
                style={{ width:'100%', padding:'7px 12px', fontSize:12, border:'1px solid #e4e4e7', borderRadius:7, outline:'none', background:'#fff' }}
              />
            </div>
            <div style={S.tableWrap}>
              {filtered.length === 0 ? (
                <div style={{ padding:32, textAlign:'center', color:'#a1a1aa', fontSize:13 }}>No records found</div>
              ) : (
                <table style={S.table}>
                  <thead style={S.thead}>
                    <tr>
                      <th style={{ ...S.th, width:36 }}>#</th>
                      {subModal.columns.map((col, ci) => (
                        <th key={ci} style={{ ...S.th, textAlign: col.format === 'currency' ? 'right' : 'left' }}>{col.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((row, ri) => (
                      <tr key={ri} style={S.tr} onMouseEnter={e=>e.currentTarget.style.background='#f8fafc'} onMouseLeave={e=>e.currentTarget.style.background=''}>
                        <td style={S.tdMuted}>{ri + 1}</td>
                        {subModal.columns.map((col, ci) => {
                          const val = row[col.key];
                          if (col.format === 'currency') {
                            return <td key={ci} style={S.tdAmt}>{fmt(val)}</td>;
                          } else if (col.format === 'date') {
                            return <td key={ci} style={S.tdDate}>{val?.split('T')[0] || '-'}</td>;
                          }
                          return <td key={ci} style={S.tdBold}>{val || '-'}</td>;
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div style={S.footer}>
              <button style={{ ...S.btnPrimary, background:'linear-gradient(135deg,#1e293b,#334155)', flex:1, justifyContent:'center' }}
                onClick={() => { setSubModal({ open:false, title:'', data:[], columns:[] }); setSubModalSearch(''); }}>Close</button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* ── KPI Summary Popup ───────────────────────────────────────────── */}
      {kpiModal.open && kpiModal.kpi && (() => {
        const k = kpiModal.kpi;
        const colorMap = {
          receivable: { grad:'linear-gradient(135deg,#38bdf8,#0284c7)', light:'#f0f9ff', accent:'#0ea5e9' },
          payable:    { grad:'linear-gradient(135deg,#c084fc,#7c3aed)', light:'#faf5ff', accent:'#a855f7' },
          revenue:    { grad:'linear-gradient(135deg,#4ade80,#16a34a)', light:'#f0fdf4', accent:'#22c55e' },
          purchase:   { grad:'linear-gradient(135deg,#fb923c,#c2410c)', light:'#fff7ed', accent:'#f97316' },
          stock:      { grad:'linear-gradient(135deg,#2dd4bf,#0f766e)', light:'#f0fdfa', accent:'#14b8a6' },
          cashflow:   { grad:'linear-gradient(135deg,#f472b6,#be185d)', light:'#fdf2f8', accent:'#ec4899' },
        };
        const c = colorMap[k.key] || colorMap.revenue;
        const summaryMsg = {
          receivable:`You have ${k.value} in outstanding receivables. Monitor overdue accounts to maintain healthy cash flow.`,
          payable:   `Total payables stand at ${k.value}. Timely payments maintain strong vendor relationships.`,
          revenue:   `This month's revenue is ${k.value}. Keep tracking daily sales to stay on target.`,
          purchase:  `Purchases this month total ${k.value}. Review pending POs to optimize procurement.`,
          stock:     `Stock valued at ${k.value}. Address low-stock items promptly to avoid lost sales.`,
          cashflow:  `Net cash flow is ${k.value}. ${parseFloat(k.value.replace(/[₹,L Cr]/g,''))>=0?'Positive flow — good health.':'Negative flow — review expenses.'}`,
        };
        const iconEl = {
          receivable:<AccountBalance style={{ fontSize:24,color:'#fff' }}/>,
          payable:   <Payment style={{ fontSize:24,color:'#fff' }}/>,
          revenue:   <TrendingUp style={{ fontSize:24,color:'#fff' }}/>,
          purchase:  <ShoppingCart style={{ fontSize:24,color:'#fff' }}/>,
          stock:     <Inventory style={{ fontSize:24,color:'#fff' }}/>,
          cashflow:  <Assessment style={{ fontSize:24,color:'#fff' }}/>,
        };
        return (
          <div style={{ ...S.overlay, zIndex:1500 }} onClick={() => setKpiModal({open:false,kpi:null})}>
            <div style={{ ...S.modal, maxWidth:340, animation:'kpiPopIn 0.22s cubic-bezier(0.34,1.56,0.64,1)' }} onClick={e=>e.stopPropagation()}>
              <div style={{ ...S.hdr, background:c.grad, alignItems:'center', gap:12 }}>
                <div style={{ width:42,height:42,borderRadius:10,background:'rgba(255,255,255,0.2)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0 }}>
                  {iconEl[k.key]}
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ color:'rgba(255,255,255,0.75)',fontSize:9,fontWeight:700,textTransform:'uppercase',letterSpacing:0.5 }}>{k.label}</div>
                  <div style={{ color:'#fff',fontSize:22,fontWeight:800,letterSpacing:-0.5,lineHeight:1.1 }}>{k.value}</div>
                </div>
                <button style={S.xBtn} onClick={() => setKpiModal({open:false,kpi:null})}>×</button>
              </div>
              <div style={{ padding:'12px 14px' }}>
                <div style={{ background:c.light, borderRadius:8, padding:'9px 11px', marginBottom:10, fontSize:11, color:'#52525b', lineHeight:1.55 }}>
                  {summaryMsg[k.key]}
                </div>
                <div style={{ fontSize:9,fontWeight:700,color:'#a1a1aa',textTransform:'uppercase',letterSpacing:0.5,marginBottom:6 }}>Breakdown</div>
                <div style={{ display:'flex',flexDirection:'column',gap:3 }}>
                  {k.sub.map((s,i) => (
                    <div key={i} style={{ display:'flex',justifyContent:'space-between',alignItems:'center',padding:'6px 10px',borderRadius:7,background:c.light,cursor:'pointer',transition:'box-shadow 0.15s' }}
                      onClick={() => { setKpiModal({open:false,kpi:null}); handleSubClick(k.key, s.l, { stopPropagation:()=>{} }); }}
                      onMouseEnter={e=>e.currentTarget.style.boxShadow='0 2px 8px rgba(0,0,0,0.08)'}
                      onMouseLeave={e=>e.currentTarget.style.boxShadow='none'}>
                      <span style={{ fontSize:11,color:'#71717a' }}>{s.l}</span>
                      <span style={{ fontSize:12,fontWeight:800,
                        color:s.c==='pos'?'#16a34a':s.c==='neg'?'#dc2626':s.c==='warn'?'#d97706':c.accent }}>
                        {s.v}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              <div style={S.footer}>
                <button style={{ ...S.btnPrimary, background:c.grad, flex:1, justifyContent:'center' }} onClick={() => setKpiModal({open:false,kpi:null})}>Close</button>
              </div>
            </div>
            <style>{`@keyframes kpiPopIn{from{opacity:0;transform:scale(0.9) translateY(12px)}to{opacity:1;transform:scale(1) translateY(0)}}`}</style>
          </div>
        );
      })()}

    </div>
  );};

export default Home;

